import { c as _c } from "react/compiler-runtime";
// Шаг «Проверка»: сводка и главная кнопка загрузки, карточка магазина-
// черновика, таблица позиций (до 3000) с фильтром, поиском, страницами,
// выбором строк и массовыми правками.
import { Loader2, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import type { CatalogImportBulkPatch, CatalogImportItem, CatalogImportItemStatus, CatalogImportJobDetail } from "@osmix/contracts";
import { useBulkUpdateImportItems, useCommitImport, useUpdateImportJob } from "@/api/hooks/use-catalog-import";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { FilterChips } from "@/components/filter-chips";
import { Segmented } from "@/components/segmented";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { useDataTable } from "@/lib/use-data-table";
import { cn } from "@/lib/utils";
import { CategorySelect, useCategoryLeaves } from "./category-select";
import { ShopDraftCard } from "./shop-draft-card";
import { ITEM_STATUS_LABELS, ImportThumb, ItemStatusPill, StatTile, errorText, formatUsd, plural, priceRange, stockSum, targetText, variantSummary } from "./shared";
type SortKey = "position" | "name" | "price" | "status";
const STATUS_ORDER: CatalogImportItemStatus[] = ["error", "failed", "warning", "ready", "committed", "excluded"];
const minPrice = (item: CatalogImportItem) => {
  const prices = item.data.variants.map(v => v.price).filter((p): p is number => p != null);
  return prices.length ? Math.min(...prices) : null;
};
export function countItems(items: CatalogImportItem[]) {
  const by = (s: CatalogImportItemStatus) => items.filter(i => i.status === s).length;
  return {
    ready: by("ready"),
    warning: by("warning"),
    error: by("error"),
    excluded: by("excluded"),
    committed: by("committed"),
    failed: by("failed")
  };
}
export function ReviewStep(t0) {
  const $ = _c(78);
  const {
    job,
    canEdit,
    onOpenItem
  } = t0;
  const editable = canEdit && job.status === "review";
  let t1;
  if ($[0] !== job.items) {
    t1 = countItems(job.items);
    $[0] = job.items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const counts = t1;
  const loadable = counts.ready + counts.warning;
  const commit = useCommitImport(job.id);
  const updateJob = useUpdateImportJob(job.id);
  const [mode, setMode] = useState(job.options.publishMode);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState(null);
  const newShop = job.target.kind === "new_shop";
  let t2;
  if ($[2] !== job.shop?.name || $[3] !== job.shopDraft?.shopName || $[4] !== newShop) {
    t2 = newShop ? job.shopDraft?.shopName?.trim() : job.shop?.name;
    $[2] = job.shop?.name;
    $[3] = job.shopDraft?.shopName;
    $[4] = newShop;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const shopName = t2;
  let t3;
  if ($[6] !== commit || $[7] !== job.options.publishMode || $[8] !== mode || $[9] !== updateJob) {
    t3 = async () => {
      setError(null);
      ;
      try {
        if (mode !== job.options.publishMode) {
          await updateJob.mutateAsync({
            options: {
              publishMode: mode
            }
          });
        }
        await commit.mutateAsync(undefined);
        setConfirm(false);
      } catch (t4) {
        const e = t4;
        setError(errorText(e));
        setConfirm(false);
      }
    };
    $[6] = commit;
    $[7] = job.options.publishMode;
    $[8] = mode;
    $[9] = updateJob;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const runCommit = t3;
  const t4 = counts.ready ? "good" : "default";
  let t5;
  if ($[11] !== counts.ready || $[12] !== t4) {
    t5 = <StatTile label={"\u0413\u043E\u0442\u043E\u0432\u043E"} value={counts.ready} tone={t4} />;
    $[11] = counts.ready;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = counts.warning ? "warning" : "default";
  let t7;
  if ($[14] !== counts.warning || $[15] !== t6) {
    t7 = <StatTile label={"\u041F\u0440\u043E\u0432\u0435\u0440\u0438\u0442\u044C"} value={counts.warning} tone={t6} hint={"\u0417\u0430\u0433\u0440\u0443\u0437\u044F\u0442\u0441\u044F, \u043D\u043E \u043B\u0443\u0447\u0448\u0435 \u043F\u043E\u0441\u043C\u043E\u0442\u0440\u0435\u0442\u044C"} />;
    $[14] = counts.warning;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = counts.error ? "bad" : "default";
  let t9;
  if ($[17] !== counts.error || $[18] !== t8) {
    t9 = <StatTile label={"\u0421 \u043E\u0448\u0438\u0431\u043A\u0430\u043C\u0438"} value={counts.error} tone={t8} hint={"\u041D\u0435 \u0437\u0430\u0433\u0440\u0443\u0437\u044F\u0442\u0441\u044F, \u043F\u043E\u043A\u0430 \u043D\u0435 \u043F\u043E\u043F\u0440\u0430\u0432\u0438\u0442\u0435"} />;
    $[17] = counts.error;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== counts.excluded) {
    t10 = <StatTile label={"\u0418\u0441\u043A\u043B\u044E\u0447\u0435\u043D\u043E"} value={counts.excluded} tone="muted" />;
    $[20] = counts.excluded;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== job.usage.costUsd) {
    t11 = formatUsd(job.usage.costUsd);
    $[22] = job.usage.costUsd;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <StatTile label={"\u041F\u043E\u0442\u0440\u0430\u0447\u0435\u043D\u043E"} value={t11} hint={"\u0420\u0430\u0441\u0445\u043E\u0434 \u043D\u0430 \u043C\u043E\u0434\u0435\u043B\u044C \u043F\u043E \u0434\u0430\u043D\u043D\u044B\u043C OpenRouter"} />;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12 || $[28] !== t5 || $[29] !== t7 || $[30] !== t9) {
    t13 = <div className="grid min-w-0 flex-1 grid-cols-3 gap-2 sm:grid-cols-5 xl:max-w-[640px]">{t5}{t7}{t9}{t10}{t12}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t5;
    $[29] = t7;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== commit.isPending || $[33] !== editable || $[34] !== loadable || $[35] !== mode) {
    t14 = editable && <div className="flex flex-col gap-2 sm:flex-row sm:items-center xl:justify-end"><Segmented value={mode} onChange={setMode} options={[{
        value: "draft",
        label: "\u0427\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u0430\u043C\u0438"
      }, {
        value: "moderation",
        label: "\u041D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E"
      }]} /><Button disabled={loadable === 0 || commit.isPending} onClick={() => setConfirm(true)}>{commit.isPending ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}Загрузить {loadable} {plural(loadable, "\u0442\u043E\u0432\u0430\u0440", "\u0442\u043E\u0432\u0430\u0440\u0430", "\u0442\u043E\u0432\u0430\u0440\u043E\u0432")}</Button></div>;
    $[32] = commit.isPending;
    $[33] = editable;
    $[34] = loadable;
    $[35] = mode;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== error) {
    t15 = error && <p role="alert" className="text-sm text-destructive xl:basis-full">{error}</p>;
    $[37] = error;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t15) {
    t16 = <section className="z-20 flex flex-col gap-3 rounded-lg border bg-background p-3 sm:p-4 md:sticky md:top-2 xl:flex-row xl:items-center xl:justify-between">{t13}{t14}{t15}</section>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== editable || $[44] !== job || $[45] !== newShop) {
    t17 = newShop && <ShopDraftCard job={job} editable={editable} />;
    $[43] = editable;
    $[44] = job;
    $[45] = newShop;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== editable || $[48] !== job || $[49] !== onOpenItem) {
    t18 = <ItemsTable job={job} editable={editable} onOpenItem={onOpenItem} />;
    $[47] = editable;
    $[48] = job;
    $[49] = onOpenItem;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const t19 = commit.isPending || updateJob.isPending;
  const t20 = `Загрузить ${loadable} ${plural(loadable, "\u0442\u043E\u0432\u0430\u0440", "\u0442\u043E\u0432\u0430\u0440\u0430", "\u0442\u043E\u0432\u0430\u0440\u043E\u0432")}?`;
  let t21;
  if ($[51] !== runCommit) {
    t21 = () => void runCommit();
    $[51] = runCommit;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setConfirm(false);
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== job || $[55] !== newShop || $[56] !== shopName) {
    t23 = <p>{newShop ? <>Будет заведён магазин «{shopName || "\u0431\u0435\u0437 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F"}» и кабинет продавца{job.target.kind === "new_shop" && job.target.sendInvite ? "; \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0443 \u0443\u0439\u0434\u0451\u0442 \u043F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435." : ". \u041F\u0440\u0438\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0443 \u043D\u0435 \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u0442\u0441\u044F \u2014 \u0435\u0433\u043E \u043C\u043E\u0436\u043D\u043E \u0432\u044B\u0441\u043B\u0430\u0442\u044C \u0438\u0437 \u0437\u0430\u044F\u0432\u043A\u0438."}</> : <>Товары появятся в магазине «{targetText(job)}».</>}</p>;
    $[54] = job;
    $[55] = newShop;
    $[56] = shopName;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const t24 = mode === "draft" ? "\u0427\u0435\u0440\u043D\u043E\u0432\u0438\u043A\u0430\u043C\u0438: \u043D\u0430 \u0432\u0438\u0442\u0440\u0438\u043D\u0435 \u0438\u0445 \u043D\u0435 \u0431\u0443\u0434\u0435\u0442, \u043F\u043E\u043A\u0430 \u043F\u0440\u043E\u0434\u0430\u0432\u0435\u0446 \u043D\u0435 \u043E\u0442\u043F\u0440\u0430\u0432\u0438\u0442 \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E." : "\u0421\u0440\u0430\u0437\u0443 \u043D\u0430 \u043C\u043E\u0434\u0435\u0440\u0430\u0446\u0438\u044E: \u043A\u0430\u0440\u0442\u043E\u0447\u043A\u0438 \u0432\u0441\u0442\u0430\u043D\u0443\u0442 \u0432 \u043E\u0447\u0435\u0440\u0435\u0434\u044C \u043C\u043E\u0434\u0435\u0440\u0430\u0442\u043E\u0440\u043E\u0432, \u043F\u0440\u043E\u0432\u0435\u0440\u043A\u0438 \u0438 \u0433\u0435\u0439\u0442 \u0446\u0435\u043D\u044B \u2014 \u043A\u0430\u043A \u0443 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u0430.";
  let t25;
  if ($[58] !== t24) {
    t25 = <p className="text-muted-foreground">{t24}</p>;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== counts.error || $[61] !== counts.excluded) {
    t26 = counts.error + counts.excluded > 0 && <p className="text-muted-foreground">Не загрузятся: с ошибками — {counts.error}, исключённых — {counts.excluded}.</p>;
    $[60] = counts.error;
    $[61] = counts.excluded;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t23 || $[64] !== t25 || $[65] !== t26) {
    t27 = <div className="space-y-2 text-sm">{t23}{t25}{t26}</div>;
    $[63] = t23;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== confirm || $[68] !== t19 || $[69] !== t20 || $[70] !== t21 || $[71] !== t27) {
    t28 = <ConfirmDialog open={confirm} busy={t19} title={t20} confirmLabel={"\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C"} onConfirm={t21} onClose={t22}>{t27}</ConfirmDialog>;
    $[67] = confirm;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t16 || $[74] !== t17 || $[75] !== t18 || $[76] !== t28) {
    t29 = <div className="flex flex-col gap-4">{t16}{t17}{t18}{t28}</div>;
    $[73] = t16;
    $[74] = t17;
    $[75] = t18;
    $[76] = t28;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  return t29;
}
function ItemsTable(t0) {
  const $ = _c(94);
  const {
    job,
    editable,
    onOpenItem
  } = t0;
  const {
    pathOf
  } = useCategoryLeaves();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selected, setSelected] = useState(t1);
  const [bulkMode, setBulkMode] = useState(null);
  const [bulkCategory, setBulkCategory] = useState(null);
  const [bulkBrand, setBulkBrand] = useState("");
  const [markup, setMarkup] = useState("100");
  const [bulkMessage, setBulkMessage] = useState(null);
  const bulk = useBulkUpdateImportItems(job.id);
  const counts = countItems(job.items);
  const q = search.trim().toLowerCase();
  const filtered = job.items.filter(item => (!status || item.status === status) && (!q || `${item.data.name} ${item.data.article} ${item.data.brand}`.toLowerCase().includes(q)));
  const table = useDataTable({
    rows: filtered,
    pageSize: 50,
    initialSort: {
      key: "position",
      dir: "asc"
    },
    accessors: {
      position: _temp,
      name: _temp2,
      price: _temp3,
      status: _temp4
    }
  });
  const sliceKey = `${status}|${q}`;
  let t2;
  if ($[1] !== table) {
    t2 = () => {
      table.setPage(1);
      setSelected(new Set());
    };
    $[1] = table;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== sliceKey) {
    t3 = [sliceKey];
    $[3] = sliceKey;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== editable) {
    t4 = item_0 => editable && item_0.status !== "committed";
    $[5] = editable;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const selectable = t4;
  let pageIds;
  let t5;
  if ($[7] !== selectable || $[8] !== selected || $[9] !== table.pageRows) {
    pageIds = table.pageRows.filter(selectable).map(_temp5);
    t5 = pageIds.length > 0 && pageIds.every(id => selected.has(id));
    $[7] = selectable;
    $[8] = selected;
    $[9] = table.pageRows;
    $[10] = pageIds;
    $[11] = t5;
  } else {
    pageIds = $[10];
    t5 = $[11];
  }
  const allOnPage = t5;
  const filteredIds = filtered.filter(selectable).map(_temp6);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = id_0 => setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id_0)) {
        next.delete(id_0);
      } else {
        next.add(id_0);
      }
      return next;
    });
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const toggle = t6;
  let t7;
  if ($[13] !== allOnPage || $[14] !== pageIds) {
    t7 = () => setSelected(prev_0 => {
      const next_0 = new Set(prev_0);
      for (const id_1 of pageIds) {
        if (allOnPage) {
          next_0.delete(id_1);
        } else {
          next_0.add(id_1);
        }
      }
      return next_0;
    });
    $[13] = allOnPage;
    $[14] = pageIds;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const togglePage = t7;
  let t8;
  bb0: {
    const sort = table.sort;
    if (!sort) {
      t8 = filtered.map(_temp7);
      break bb0;
    }
    const acc = {
      position: _temp8,
      name: _temp9,
      price: _temp0,
      status: _temp1
    }[sort.key];
    t8 = [...filtered].sort((a, b) => {
      const x = acc(a);
      const y = acc(b);
      const r = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "ru");
      return sort.dir === "asc" ? r : -r;
    }).map(_temp10);
  }
  const order = t8;
  const open = item_1 => onOpenItem(item_1.id, order);
  let t9;
  if ($[16] !== bulk || $[17] !== selected) {
    t9 = async (set, label) => {
      setBulkMessage(null);
      ;
      try {
        const res = await bulk.mutateAsync({
          itemIds: [...selected],
          set
        });
        setBulkMessage({
          ok: true,
          text: `${label}: позиций — ${res.updated}`
        });
        setBulkMode(null);
        setSelected(new Set());
      } catch (t10) {
        const e = t10;
        setBulkMessage({
          ok: false,
          text: errorText(e)
        });
      }
    };
    $[16] = bulk;
    $[17] = selected;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const applyBulk = t9;
  let t10;
  if ($[19] !== markup) {
    t10 = markup.replace(",", ".");
    $[19] = markup;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const markupValue = Number(t10);
  let t11;
  if ($[21] !== markup || $[22] !== markupValue) {
    t11 = markup.trim() !== "" && Number.isFinite(markupValue) && markupValue >= -90 && markupValue <= 500;
    $[21] = markup;
    $[22] = markupValue;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const markupValid = t11;
  const chips = (["ready", "warning", "error", "excluded", "committed", "failed"] as const).filter(s => s === "ready" || s === "warning" || s === "error" || s === "excluded" || counts[s] > 0).map(s_0 => ({
    value: s_0,
    label: ITEM_STATUS_LABELS[s_0],
    count: counts[s_0],
    tone: (s_0 === "error" || s_0 === "failed" ? "danger" : s_0 === "warning" ? "warning" : "default") as "danger" | "warning" | "default"
  }));
  let t12;
  if ($[24] !== job.items.length) {
    t12 = <h2 className="font-semibold">Позиции · {job.items.length}</h2>;
    $[24] = job.items.length;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e_0 => setSearch(e_0.target.value);
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== search) {
    t14 = <Input value={search} onChange={t13} placeholder={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435, \u0430\u0440\u0442\u0438\u043A\u0443\u043B, \u0431\u0440\u0435\u043D\u0434"} aria-label={"\u041F\u043E\u0438\u0441\u043A \u043F\u043E \u043F\u043E\u0437\u0438\u0446\u0438\u044F\u043C"} className="h-9 w-full sm:max-w-xs" />;
    $[27] = search;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14) {
    t15 = <div className="flex flex-wrap items-center justify-between gap-2">{t12}{t14}</div>;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== chips || $[33] !== job.items.length || $[34] !== status) {
    t16 = <FilterChips value={status} onChange={setStatus} chips={chips} allLabel={"\u0412\u0441\u0435"} allCount={job.items.length} />;
    $[32] = chips;
    $[33] = job.items.length;
    $[34] = status;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== bulkMessage || $[37] !== selected.size) {
    t17 = bulkMessage && !selected.size && <p role="status" className={cn("text-sm", bulkMessage.ok ? "text-muted-foreground" : "text-destructive")}>{bulkMessage.text}</p>;
    $[36] = bulkMessage;
    $[37] = selected.size;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== table.total) {
    t18 = table.total === 0 && <li className="p-4 text-center text-sm text-muted-foreground">Ничего не нашлось</li>;
    $[39] = table.total;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const t19 = "hidden md:block";
  const T0 = Table;
  let t20;
  if ($[41] !== allOnPage || $[42] !== editable || $[43] !== togglePage) {
    t20 = editable && <input type="checkbox" className="size-4 align-middle" aria-label={"\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443"} checked={allOnPage} onChange={togglePage} />;
    $[41] = allOnPage;
    $[42] = editable;
    $[43] = togglePage;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <TableHead className="w-10">{t20}</TableHead>;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== table.sort || $[48] !== table.toggleSort) {
    t22 = <SortableHead label={"\u2116"} sortKey="position" sort={table.sort} onToggle={table.toggleSort} className="w-12" />;
    $[47] = table.sort;
    $[48] = table.toggleSort;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <TableHead className="w-14">Фото</TableHead>;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== table.sort || $[52] !== table.toggleSort) {
    t24 = <SortableHead label={"\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435"} sortKey="name" sort={table.sort} onToggle={table.toggleSort} />;
    $[51] = table.sort;
    $[52] = table.toggleSort;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <TableHead>Категория</TableHead>;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== table.sort || $[56] !== table.toggleSort) {
    t26 = <SortableHead label={"\u0426\u0435\u043D\u0430"} sortKey="price" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />;
    $[55] = table.sort;
    $[56] = table.toggleSort;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <TableHead>Размеры · цвета</TableHead>;
    t28 = <TableHead className="hidden text-right xl:table-cell">Остаток</TableHead>;
    $[58] = t27;
    $[59] = t28;
  } else {
    t27 = $[58];
    t28 = $[59];
  }
  let t29;
  if ($[60] !== table.sort || $[61] !== table.toggleSort) {
    t29 = <SortableHead label={"\u0421\u0442\u0430\u0442\u0443\u0441"} sortKey="status" sort={table.sort} onToggle={table.toggleSort} />;
    $[60] = table.sort;
    $[61] = table.toggleSort;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t21 || $[64] !== t22 || $[65] !== t24 || $[66] !== t26 || $[67] !== t29) {
    t30 = <TableHeader><TableRow>{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}</TableRow></TableHeader>;
    $[63] = t21;
    $[64] = t22;
    $[65] = t24;
    $[66] = t26;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  const T1 = TableBody;
  let t31;
  if ($[69] !== table.total) {
    t31 = table.total === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Ничего не нашлось</TableCell></TableRow>;
    $[69] = table.total;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const t32 = table.pageRows.map(item_3 => {
    const category = pathOf(item_3.data.categoryId, item_3.data.categoryPath);
    const stock = stockSum(item_3.data.variants);
    return <TableRow key={item_3.id} className={cn("cursor-pointer", item_3.status === "excluded" && "opacity-60")} data-state={selected.has(item_3.id) ? "selected" : undefined} onClick={() => open(item_3)}><TableCell onClick={_temp11}>{selectable(item_3) && <input type="checkbox" className="size-4 align-middle" aria-label={`Выбрать «${item_3.data.name}»`} checked={selected.has(item_3.id)} onChange={() => toggle(item_3.id)} />}</TableCell><TableCell className="text-muted-foreground tabular-nums">{item_3.position}</TableCell><TableCell><ImportThumb jobId={job.id} fileId={item_3.data.photoFileIds[0]} url={item_3.data.photoFileIds[0] ? null : item_3.data.photoUrls[0]} /></TableCell><TableCell className="max-w-[240px]"><span className="block truncate font-medium" title={item_3.data.name}>{item_3.data.name || "\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F"}</span><span className="block truncate text-xs text-muted-foreground">{[item_3.data.article, item_3.data.brand].filter(Boolean).join(" \xB7 ") || "\u0431\u0435\u0437 \u0430\u0440\u0442\u0438\u043A\u0443\u043B\u0430"}</span></TableCell><TableCell className="max-w-[170px]">{category ? <span className="block truncate text-muted-foreground" title={category}>{category.split(" / ").slice(-2).join(" / ")}</span> : <span className="text-destructive">не определена</span>}</TableCell><TableCell className={cn("whitespace-nowrap text-right tabular-nums", minPrice(item_3) == null && "text-destructive")}>{priceRange(item_3.data.variants)}</TableCell><TableCell className="max-w-[160px] truncate text-muted-foreground" title={variantSummary(item_3.data.variants)}>{variantSummary(item_3.data.variants)}</TableCell><TableCell className="hidden text-right tabular-nums xl:table-cell">{stock ?? "\u2014"}</TableCell><TableCell className="w-[200px] max-w-[200px] whitespace-normal"><ItemStatusPill status={item_3.status} />{item_3.issues[0] && item_3.status !== "committed" && <span className={cn("mt-1 line-clamp-2 text-xs", item_3.issues[0].level === "error" ? "text-destructive" : "text-amber-700 dark:text-amber-500")} title={item_3.issues.map(_temp12).join("\n")}>{item_3.issues[0].message}{item_3.issues.length > 1 ? ` · ещё ${item_3.issues.length - 1}` : ""}</span>}</TableCell></TableRow>;
  });
  let t33;
  if ($[71] !== T1 || $[72] !== t31 || $[73] !== t32) {
    t33 = <T1>{t31}{t32}</T1>;
    $[71] = T1;
    $[72] = t31;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== T0 || $[76] !== t30 || $[77] !== t33) {
    t34 = <div className={t19}><T0>{t30}{t33}</T0></div>;
    $[75] = T0;
    $[76] = t30;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== allOnPage || $[80] !== applyBulk || $[81] !== bulk || $[82] !== bulkBrand || $[83] !== bulkCategory || $[84] !== bulkMessage || $[85] !== bulkMode || $[86] !== filteredIds || $[87] !== markup || $[88] !== markupValid || $[89] !== markupValue || $[90] !== selected.size) {
    t35 = selected.size > 0 && <div className="sticky bottom-2 z-10 flex flex-col gap-2 rounded-md border bg-background p-2 shadow-md"><div className="flex flex-wrap items-center gap-2"><span className="px-2 text-sm font-medium">Выбрано: {selected.size}</span>{allOnPage && filteredIds.length > selected.size && <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(filteredIds))}>Выбрать все {filteredIds.length} в фильтре</Button>}<Button size="sm" variant={bulkMode === "category" ? "secondary" : "outline"} onClick={() => setBulkMode(bulkMode === "category" ? null : "category")}>Категория</Button><Button size="sm" variant={bulkMode === "brand" ? "secondary" : "outline"} onClick={() => setBulkMode(bulkMode === "brand" ? null : "brand")}>Бренд</Button><Button size="sm" variant={bulkMode === "markup" ? "secondary" : "outline"} onClick={() => setBulkMode(bulkMode === "markup" ? null : "markup")}>Наценка %</Button><Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void applyBulk({
          excluded: true
        }, "\u0418\u0441\u043A\u043B\u044E\u0447\u0435\u043D\u043E")}>Исключить</Button><Button size="sm" variant="outline" disabled={bulk.isPending} onClick={() => void applyBulk({
          excluded: false
        }, "\u0412\u043E\u0437\u0432\u0440\u0430\u0449\u0435\u043D\u043E")}>Вернуть</Button><Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Снять выбор</Button>{bulk.isPending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}</div>{bulkMode === "category" && <div className="flex flex-col gap-2 sm:flex-row sm:items-center"><div className="grid min-w-0 flex-1"><CategorySelect value={bulkCategory} onChange={id_2 => setBulkCategory(id_2)} aria-label={"\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F \u0434\u043B\u044F \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u044B\u0445"} emptyLabel={"\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044E"} /></div><Button size="sm" disabled={!bulkCategory || bulk.isPending} onClick={() => void applyBulk({
          categoryId: bulkCategory
        }, "\u041A\u0430\u0442\u0435\u0433\u043E\u0440\u0438\u044F \u043F\u043E\u0441\u0442\u0430\u0432\u043B\u0435\u043D\u0430")}>Поставить</Button></div>}{bulkMode === "brand" && <div className="flex flex-col gap-2 sm:flex-row sm:items-center"><Input value={bulkBrand} onChange={e_2 => setBulkBrand(e_2.target.value)} placeholder={"\u0411\u0440\u0435\u043D\u0434"} aria-label={"\u0411\u0440\u0435\u043D\u0434 \u0434\u043B\u044F \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u044B\u0445"} className="h-8 sm:max-w-xs" /><Button size="sm" disabled={bulk.isPending} onClick={() => void applyBulk({
          brand: bulkBrand.trim()
        }, "\u0411\u0440\u0435\u043D\u0434 \u043F\u043E\u0441\u0442\u0430\u0432\u043B\u0435\u043D")}>Поставить</Button></div>}{bulkMode === "markup" && <div className="flex flex-col gap-2 sm:flex-row sm:items-center"><Input value={markup} onChange={e_3 => setMarkup(e_3.target.value)} inputMode="decimal" aria-label={"\u041D\u0430\u0446\u0435\u043D\u043A\u0430 \u0432 \u043F\u0440\u043E\u0446\u0435\u043D\u0442\u0430\u0445"} aria-invalid={!markupValid || undefined} className="h-8 sm:w-28" /><span className="text-xs text-muted-foreground">% к цене и старой цене: 100 — опт ×2, −10 — скидка 10 %. От −90 до 500.</span><Button size="sm" disabled={!markupValid || bulk.isPending} onClick={() => void applyBulk({
          priceMarkupPercent: markupValue
        }, "\u041D\u0430\u0446\u0435\u043D\u043A\u0430 \u043F\u0440\u0438\u043C\u0435\u043D\u0435\u043D\u0430")}>Применить</Button></div>}{bulkMessage && !bulkMessage.ok && <p className="text-sm text-destructive">{bulkMessage.text}</p>}</div>;
    $[79] = allOnPage;
    $[80] = applyBulk;
    $[81] = bulk;
    $[82] = bulkBrand;
    $[83] = bulkCategory;
    $[84] = bulkMessage;
    $[85] = bulkMode;
    $[86] = filteredIds;
    $[87] = markup;
    $[88] = markupValid;
    $[89] = markupValue;
    $[90] = selected.size;
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  let t36;
  if ($[92] !== table) {
    t36 = table.total > 0 && <DataTablePagination table={table} />;
    $[92] = table;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  return <section className="flex flex-col gap-3">{t15}{t16}{t17}<ul className="flex flex-col gap-2 md:hidden">{table.pageRows.map(item_2 => <li key={item_2.id} className={cn("flex gap-3 rounded-lg border p-3", selected.has(item_2.id) && "border-primary bg-primary/5")}>{selectable(item_2) && <input type="checkbox" className="mt-1 size-4 shrink-0" aria-label={`Выбрать «${item_2.data.name}»`} checked={selected.has(item_2.id)} onChange={() => toggle(item_2.id)} />}<button type="button" onClick={() => open(item_2)} className="flex min-w-0 flex-1 gap-3 text-left"><ImportThumb jobId={job.id} fileId={item_2.data.photoFileIds[0]} url={item_2.data.photoFileIds[0] ? null : item_2.data.photoUrls[0]} className="h-16 w-12" /><span className="flex min-w-0 flex-1 flex-col gap-1"><span className="line-clamp-2 text-sm font-medium">{item_2.data.name || "\u0411\u0435\u0437 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u044F"}</span><span className="text-xs text-muted-foreground tabular-nums">{priceRange(item_2.data.variants)} · {variantSummary(item_2.data.variants)}</span><span className="flex flex-wrap items-center gap-2"><ItemStatusPill status={item_2.status} />{item_2.issues[0] && <span className={cn("min-w-0 truncate text-xs", item_2.issues[0].level === "error" ? "text-destructive" : "text-amber-700 dark:text-amber-500")}>{item_2.issues[0].message}</span>}</span></span></button></li>)}{t18}</ul>{t34}{t35}{t36}</section>;
}
function _temp12(i_11) {
  return i_11.message;
}
function _temp11(e_1) {
  return e_1.stopPropagation();
}
function _temp10(i_10) {
  return i_10.id;
}
function _temp1(i_9) {
  return STATUS_ORDER.indexOf(i_9.status);
}
function _temp0(i_8) {
  return minPrice(i_8) ?? Number.POSITIVE_INFINITY;
}
function _temp9(i_7) {
  return i_7.data.name;
}
function _temp8(i_6) {
  return i_6.position;
}
function _temp7(i_5) {
  return i_5.id;
}
function _temp6(i_4) {
  return i_4.id;
}
function _temp5(i_3) {
  return i_3.id;
}
function _temp4(i_2) {
  return STATUS_ORDER.indexOf(i_2.status);
}
function _temp3(i_1) {
  return minPrice(i_1);
}
function _temp2(i_0) {
  return i_0.data.name;
}
function _temp(i) {
  return i.position;
}